import { useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger);

// GSAP's default lag smoothing, restored when Lenis is torn down.
const GSAP_LAG_THRESHOLD_MS = 500;
const GSAP_ADJUSTED_LAG_MS = 33;

let sharedLenis = null;

export function getLenis() {
  return sharedLenis;
}

// Exactly one loop may advance Lenis. Lenis eases by the time between
// consecutive raf() calls, so a second caller on another clock (GSAP's ticker
// used to be one) rewinds the scroll mid-frame, runs ScrollTrigger at the
// rewound position and corrupts `velocity`. Feeding the frame timestamp keeps
// the ease on the display's clock, one step per frame at any refresh rate.
function runLenisLoop(lenis) {
  let rafId;
  const step = (time) => {
    // Book the next frame first: an error thrown by a scroll listener then
    // costs one frame instead of freezing smooth scroll for the whole visit.
    rafId = requestAnimationFrame(step);
    // eslint-disable-next-line no-restricted-syntax -- the one sanctioned driver
    lenis.raf(time);
  };
  rafId = requestAnimationFrame(step);
  return () => cancelAnimationFrame(rafId);
}

// ScrollTrigger reads the new scroll position right after each Lenis step, so
// pins and scrubs move in the same frame as the page.
function syncScrollTrigger(lenis) {
  lenis.on('scroll', ScrollTrigger.update);
  return () => lenis.off('scroll', ScrollTrigger.update);
}

// Lag smoothing stays off while Lenis runs, as it was when GSAP's ticker drove
// Lenis: after a main-thread stall GSAP and Lenis agree on how much time passed,
// so scrubbed animations don't fall behind a scroll that has already moved on.
function disableGsapLagSmoothing() {
  gsap.ticker.lagSmoothing(0);
  return () => gsap.ticker.lagSmoothing(GSAP_LAG_THRESHOLD_MS, GSAP_ADJUSTED_LAG_MS);
}

export function useLenis() {
  // Lazy initial state covers the two synchronous "already ready" branches —
  // a singleton already exists, or the user prefers reduced motion (in which
  // case we never spin up Lenis at all).
  const [ready, setReady] = useState(
    () =>
      !!sharedLenis ||
      (typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  );

  useEffect(() => {
    if (sharedLenis) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    sharedLenis = lenis;
    const stopSync = syncScrollTrigger(lenis);
    const restoreLagSmoothing = disableGsapLagSmoothing();
    const stopLoop = runLenisLoop(lenis);
    // Lenis is genuinely async-to-mount — flagging this as a "cascading
    // render" misses that there's no synchronous value to seed from.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setReady(true);

    return () => {
      stopLoop();
      restoreLagSmoothing();
      stopSync();
      lenis.destroy();
      sharedLenis = null;
    };
  }, []);

  return ready;
}
